/**
 * The three coaching-preference lists offered on the coach profile.
 *
 * WHAT THESE LISTS ARE
 * --------------------
 * Suggestions, not a controlled vocabulary. Nothing downstream reads them:
 * `Coach.playing_styles`, `Coach.defensive_tactics` and `Coach.offensive_tactics`
 * are plain `JSONField(default=list)` and their serializers are
 * `ListField(child=CharField())` with no `choices` and no validation
 * (users/models.py, users/serializers.py). The backend has always taken any
 * string; the fixed dropdown in the UI was the only thing standing in the way,
 * and it was never pinned by a test, a comment or a spec. So every section here
 * also offers "Other" plus free text, exactly the way the licence dialog does
 * (components/profile/modals/AddLicenseModal.js) — the typed wording is stored
 * verbatim, and a coach who already saved a term by hand keeps seeing it,
 * because the profile renders what is stored and never looks it up here.
 *
 * WHY OFFENSIVE_TACTICS EXISTS
 * ----------------------------
 * It did not, anywhere. A coach could state how his team defends but not how it
 * attacks. This list is built as the counterpart of the defensive one, entry
 * for entry in kind: where the defensive list names marking schemes, block
 * height, traps, structural roles and set pieces, this one names build-up,
 * full-back roles, running patterns, structural roles and set pieces. The words
 * are mechanisms, deliberately not styles — "Wing Play", "Counter-attack" and
 * "Direct Play" already live in PLAYING_STYLE_OPTIONS and must not be said
 * twice in two different boxes.
 *
 * THESE ARE PREFERENCES
 * ---------------------
 * Nothing in here is a reported fact about a match, a season or a result. The
 * profile says so above the three sections (TACTICS_PREFERENCE_NOTE). Keep that
 * true: no counting, no verification, no filtering on these values.
 *
 * TRANSLATION
 * -----------
 * Every label below is rendered English text and therefore a key in the flat
 * 'static' namespace. Each has a row with all 16 locale columns in
 * `translations/components/profile/coach-profile-sections.json`. Renaming a
 * label here without moving its row there drops it back to machine translation,
 * and for Croatian to permanent English (STATIC_ONLY_LANGS).
 */

export const PLAYING_STYLE_OPTIONS = [
  'Possession-based',
  'Counter-attack',
  'High Press',
  'Direct Play',
  'Wing Play',
  'Tiki-taka',
  'Park the Bus',
  'Gegenpressing',
  'Total Football',
  'Route One',
];

export const DEFENSIVE_TACTICS_OPTIONS = [
  'Zonal Marking',
  'Man Marking',
  'Counter-press',
  'Low Block',
  'High Line',
  'Offside Trap',
  'Pressing Trap',
  'Double Pivot',
  'Sweeper Keeper',
  'Compact Defense',
];

export const OFFENSIVE_TACTICS_OPTIONS = [
  'Build-up from the Back',
  'Overlapping Full-Backs',
  'Inverted Full-Backs',
  'Half-Space Runs',
  'Third-Man Combinations',
  'False Nine',
  'Overloads and Switches',
  'Late Runs into the Box',
  'Crossing from Wide Areas',
  'Attacking Set Pieces',
];

/**
 * The dropdown entry that opens the free-text field. "Other" is reused verbatim
 * from the bundle the platform already ships in all 16 languages, so no second
 * row is created for it in a namespace that is flat and global.
 */
export const OTHER_OPTION_LABEL = 'Other';

/**
 * The longest own wording a coach can add. The chips wrap in a flex row and a
 * sentence-length entry would push the section apart, so the input stops here.
 * It also sits under the 64 characters `StaffSerializer` allows for the same
 * two fields, which is the tightest limit any of these lists meets server-side.
 */
export const TACTIC_TERM_MAX_LENGTH = 40;

/**
 * The line that stands above the three sections, on the coach's own profile and
 * on the public one. It is the whole point of the block: these entries describe
 * how a coach works, they are not data the platform reports about him.
 */
export const TACTICS_PREFERENCE_NOTE =
  "These are preferences, not reported data. They reflect the coach's philosophy and way of working.";
